import { getBlobContainer } from "./helper";
import { isBlobStorageFileName } from "./musicDisplay";
import { parseMusicBlobRefFromPath } from "./jobResultBlob";

export interface MusicStorageRef {
  folder: string;
  fileName: string;
}

export function resolveFilenameFromStorageUrl(fileUrl: string): string {
  const normalized = fileUrl.replace(/\\/g, "/").split("?")[0];
  const fileName = normalized.split("/").pop();
  if (!fileName) {
    throw new Error("Could not resolve filename from storage URL.");
  }
  return fileName;
}

export function normalizeMusicJobId(value: string): string {
  const trimmed = value.trim();
  if (/^[a-f0-9]{32}$/i.test(trimmed)) {
    return `${trimmed.slice(0, 8)}-${trimmed.slice(8, 12)}-${trimmed.slice(12, 16)}-${trimmed.slice(16, 20)}-${trimmed.slice(20)}`;
  }
  return trimmed;
}

export function buildMusicBlobStorageFileName(
  musicId: string,
  extension: string,
): string {
  const base = musicId.replace(/-/g, "").toLowerCase();
  const ext = extension.replace(/^\./, "").toLowerCase() || "wav";
  return `${base}.${ext}`;
}

export function isMusicBlobStorageFileName(filename?: string | null): boolean {
  if (!filename?.trim()) {
    return false;
  }
  const trimmed = filename.trim();
  if (trimmed.startsWith(".")) {
    return false;
  }
  return isBlobStorageFileName(trimmed);
}

export function resolveMusicJobIdFromBlobFileName(
  fileName: string,
): string | undefined {
  const base = fileName.replace(/\.[^/.]+$/, "").trim();
  if (/^[a-f0-9]{32}$/i.test(base)) {
    return normalizeMusicJobId(base);
  }
  return undefined;
}

export function isMusicJobGuid(value?: string | null): boolean {
  if (!value?.trim()) {
    return false;
  }
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
}

export function resolveMusicJobIdFromCustomProps(
  customProps: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!customProps) {
    return undefined;
  }
  const raw = customProps.musicJobId ?? customProps.MusicJobId;
  if (typeof raw === "string" && raw.trim()) {
    const normalized = normalizeMusicJobId(raw.trim());
    if (isMusicJobGuid(normalized)) {
      return normalized;
    }
  }
  return undefined;
}

export function resolveMusicJobIdFromDocProfile(
  docProfile: Record<string, unknown>,
  customProps: Record<string, unknown>,
): string {
  const fromProps = resolveMusicJobIdFromCustomProps(customProps);
  if (fromProps) {
    return fromProps;
  }

  const uniqueName = docProfile.uniqueName ?? docProfile.UniqueName;
  if (typeof uniqueName === "string" && uniqueName.trim()) {
    const fromUnique = resolveMusicJobIdFromBlobFileName(uniqueName.trim());
    if (fromUnique) {
      return fromUnique;
    }
  }

  const modifiedName = docProfile.modifiedName ?? docProfile.ModifiedName;
  if (typeof modifiedName === "string" && modifiedName.trim()) {
    const fromModified = resolveMusicJobIdFromBlobFileName(modifiedName.trim());
    if (fromModified) {
      return fromModified;
    }
  }

  const fileUrl =
    (docProfile.modifieldFileUrl as string | undefined) ??
    (docProfile.originalFileUrl as string | undefined);
  if (fileUrl) {
    const blobName = resolveFilenameFromStorageUrl(fileUrl).replace(
      /\.[^/.]+$/,
      "",
    );
    if (/^[a-f0-9]{32}$/i.test(blobName)) {
      return normalizeMusicJobId(blobName);
    }
  }

  return "";
}

/** Resolves blob folder + file name from blob path or music job id (never display filename). */
export function resolveMusicBlobStorageRef(options: {
  musicId?: string | null;
  blobPath?: string | null;
  blobContainer?: string | null;
}): MusicStorageRef {
  const folder = options.blobContainer ?? getBlobContainer();

  if (options.blobPath) {
    return parseMusicBlobRefFromPath(options.blobPath, folder);
  }

  if (options.musicId) {
    return {
      folder,
      fileName: buildMusicBlobStorageFileName(options.musicId, "wav"),
    };
  }

  return { folder, fileName: "music.wav" };
}

export function resolveBlobFileNameFromDocProfile(
  docProfile: Record<string, unknown>,
): string | undefined {
  const uniqueName = docProfile.uniqueName ?? docProfile.UniqueName;
  if (typeof uniqueName === "string" && uniqueName.trim()) {
    return uniqueName.trim();
  }

  const modifiedName = docProfile.modifiedName ?? docProfile.ModifiedName;
  if (
    typeof modifiedName === "string" &&
    modifiedName.trim() &&
    isMusicBlobStorageFileName(modifiedName.trim())
  ) {
    return modifiedName.trim();
  }

  const fileUrl =
    (docProfile.modifieldFileUrl as string | undefined) ??
    (docProfile.originalFileUrl as string | undefined);
  if (fileUrl) {
    return resolveFilenameFromStorageUrl(fileUrl);
  }

  return undefined;
}

/** Editor / UI label — DisplayName from doc profile, else OriginalName. */
export function resolveDisplayNameFromDocProfile(
  docProfile: Record<string, unknown>,
): string | undefined {
  const displayName = docProfile.displayName ?? docProfile.DisplayName;
  if (typeof displayName === "string" && displayName.trim()) {
    return displayName.trim();
  }

  const original = docProfile.originalName ?? docProfile.OriginalName;
  if (typeof original === "string" && original.trim()) {
    return original.trim();
  }

  return undefined;
}
